import { Action } from '@reduxjs/toolkit';
import i18n from 'i18n';
import { selectFeatureFlag } from 'features/feature-set/featuresSlice';
import { notification } from 'features/notifications/toast/notification';
import { selectIsAudioOnlyRoom, selectRoomStatus } from 'features/room/roomSlice';
import { validateVBOptionsSaga } from 'features/sdk/validateVBOptionsSaga';
import { virtualBackgroundChangeRequested } from 'features/virtual-backgrounds/actions';
import { adaptVBOptions } from 'features/virtual-backgrounds/blurValueMap';
import { initVirtualBackground } from 'features/virtual-backgrounds/thunks/initVirtualBackground';
import {
  customBackgroundApplied,
  virtualBackgroundEnforceToggled,
  virtualBackgroundSelected,
} from 'features/virtual-backgrounds/virtualBackgroundsSlice';
import { call, put, select } from 'redux-saga/effects';
import { eventBus, VirtualBackgroundOptions } from 'utils/eventBus';
import { selectVirtualBackgroundDeviceSupported } from 'features/virtual-backgrounds/selectors';
import { logVBIntent } from 'utils/webrtc/virtual-background/gateTelemetry';

export function* onConfigureVBCommandSaga(
  options: Partial<VirtualBackgroundOptions>,
  activate: boolean
) {
  // Room state at command time, not the later read below (which decides apply-now vs sync) —
  // the intent signal must record even when the command is refused further down.
  const roomStatusAtCommand: string = yield select(selectRoomStatus);
  yield call(logVBIntent, {
    source: activate ? 'sdk-command' : 'sdk-init',
    roomJoined: roomStatusAtCommand === 'joined',
    enforce: !!options.enforce,
  });

  const deviceSupported: boolean = yield select(selectVirtualBackgroundDeviceSupported);
  if (!deviceSupported) {
    yield call(
      eventBus.notAllowedError,
      'Virtual backgrounds are not supported on this device',
      'virtual-bg'
    );

    return;
  }

  const controlEnabled: boolean = yield select(selectFeatureFlag, 'virtualBackgrounds');

  if (!controlEnabled) {
    yield call(
      eventBus.notAllowedError,
      "Virtual backgrounds are disabled. You'll need to edit this room's properties to enable them.",
      'virtual-bg'
    );

    return;
  }

  const isAudioOnly: boolean = yield select(selectIsAudioOnlyRoom);
  if (isAudioOnly) {
    yield call(
      eventBus.notAllowedError,
      'Virtual backgrounds are not available in audio-only rooms',
      'virtual-bg'
    );
    return;
  }

  const adaptedOptions = adaptVBOptions(options);
  const { valid, config, targetOption } = yield call(validateVBOptionsSaga, adaptedOptions);

  if (valid && config) {
    yield put(virtualBackgroundEnforceToggled(!!options.enforce));

    if (targetOption === 'imageUrl' || targetOption === 'videoUrl') {
      yield put(customBackgroundApplied(config));
    }

    const initVirtualBackgroundThunk: Action = yield call(initVirtualBackground);
    const initDispatchResult: unknown = yield put(initVirtualBackgroundThunk);
    // dispatching an async thunk returns a promise that settles when THIS dispatch finishes —
    // unlike take([fulfilled, rejected]), it cannot observe another worker's dispatch
    const initResult: Action = yield initDispatchResult;

    // A refused gate can never apply; recording the selection would only arm re-attempts on
    // every camera toggle. A transient failure keeps it — the deferred attempt is the retry.
    if (initVirtualBackground.rejected.match(initResult) && initResult.payload === 'unsupported') {
      yield call(eventBus.virtualBackgroundNotApplied, config, 'unsupported');
      yield call(notification, i18n.t('notifications:virtual_bg.unsupported'));
      return;
    }

    const roomStatus: string = yield select(selectRoomStatus);
    // if inside the room, apply immediately,
    // otherwise trigger side effect so that it will be updated in publishing media
    const roomJoined = roomStatus === 'joined';

    if (activate && roomJoined) {
      yield put(virtualBackgroundChangeRequested(config));
    } else {
      yield put(virtualBackgroundSelected(config, { sync: true }));
    }
  }
}
